import { VerityAssessment } from "@verity/api-client/types";
import { UseProctoringResultViewReturn } from "./useProctoringResultView";
import useAssignmentResult from "./useAssignmentResult";
import { useCallback, useEffect, useMemo, useState } from "react";
import i18n from "../../../../../i18n";
import { isBetweenTimes } from "../../../../../helpers/time";
import apiClient from "../../../../../helpers/api-client";
import { coreToast } from "@verity/components/core";
import {
	QuizQuestion,
	QuizResponseResult,
	DraftGrade,
	mapQuizDataToResults,
	validateDraftGrades,
	hasErrors,
	prepareResponsesForSave,
	getQuestionNumbersWithErrors,
	GradeValidationError,
	getDraftGradesFromResponses,
} from "./verityAssessmentResultHelper";

export type UseVerityAssessmentResultProps = {
	verityAssessment: VerityAssessment;
} & Pick<UseProctoringResultViewReturn, "session">;

export default function useVerityAssessmentResult({ verityAssessment, session }: UseVerityAssessmentResultProps) {
	const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
	const [quizResponses, setQuizResponses] = useState<QuizResponseResult[]>([]);
	const [fetchingQuestions, setFetchingQuestions] = useState(false);
	const [savingGrade, setSavingGrade] = useState<"publish" | "draft" | undefined>(undefined);
	const [unsavedModalShowing, setUnsavedModalShowing] = useState(false);
	const [pendingAction, setPendingAction] = useState<(() => void | Promise<void>) | null>(null);

	// responseId -> DraftGrade
	const [draftGrades, setDraftGrades] = useState<Record<number, DraftGrade>>({});

	const assignmentControl = useAssignmentResult({
		verityAssessment,
		session,
	});

	const numAttemptsText = useMemo(() => {
		if (verityAssessment.numAttempts <= 0) {
			return i18n.t("result.components.resultsPage.proctoringResultsViewTSX.unlimitedAttempts") as string;
		}
		return i18n.t("result.components.resultsPage.proctoringResultsViewTSX.limitedAttempts", {
			count: verityAssessment.numAttempts,
		}) as string;
	}, [verityAssessment.numAttempts]);

	const isLateSubmission = useMemo(() => {
		if (
			!verityAssessment.allowLateSubmission ||
			!verityAssessment.lateSubmissionDate ||
			!session.selectedProctoringSession.endTime
		)
			return false;
		const endTime = new Date(verityAssessment.endTime);
		const lateSubmissionDate = new Date(verityAssessment.lateSubmissionDate);
		const timeSubmitted = new Date(session.selectedProctoringSession.endTime);
		return isBetweenTimes(timeSubmitted, endTime, lateSubmissionDate);
	}, [
		verityAssessment.allowLateSubmission,
		verityAssessment.lateSubmissionDate,
		verityAssessment.endTime,
		session.selectedProctoringSession.endTime,
	]);

	useEffect(() => {
		if (!session.sessionUuid) {
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				setFetchingQuestions(true);
				const data = await apiClient.proctoringSession.getInstructorQuizSessionData(session.sessionUuid);
				if (cancelled) return;

				const { sortedQuestions, mappedResponses } = mapQuizDataToResults(data);
				setQuizQuestions(sortedQuestions);
				setQuizResponses(mappedResponses);
				setDraftGrades(getDraftGradesFromResponses(mappedResponses));
			} catch (err) {
				console.error("Failed to fetch quiz session data:", err);
			} finally {
				setFetchingQuestions(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [session.sessionUuid]);

	/**Saves all responses. Returns true if successful, false otherwise. Does validation before and shows errors*/
	const handleSaveGrade = useCallback(
		async (publish: boolean) => {
			let latestValidatedDrafts: ReturnType<typeof validateDraftGrades> = draftGrades;

			try {
				setSavingGrade(publish ? "publish" : "draft");
				latestValidatedDrafts = validateDraftGrades(draftGrades, quizResponses, quizQuestions);
				if (hasErrors(latestValidatedDrafts)) {
					throw new GradeValidationError();
				}

				const res = await apiClient.proctoringSession.updateQuizResponsesInstructor(session.sessionUuid, {
					responses: prepareResponsesForSave(draftGrades, quizResponses),
					shouldPublish: publish,
				});

				const { mappedResponses } = mapQuizDataToResults(res);
				setQuizResponses(mappedResponses);
				setDraftGrades(getDraftGradesFromResponses(mappedResponses));

				if (res.syncError && publish) {
					coreToast.warning(i18n.t(`result.components.resultsPage.proctoringResultsViewTSX.saveSyncError`) as string);
				} else {
					const key = publish ? "saveSuccessFinal" : "saveSuccessDraft";
					coreToast.success(i18n.t(`result.components.resultsPage.proctoringResultsViewTSX.${key}`) as string);
				}
				return true;
			} catch (e) {
				console.error("Failed to save grade:", e);

				let errorMessage = i18n.t("result.components.resultsPage.proctoringResultsViewTSX.saveFail") as string;
				if (e instanceof GradeValidationError) {
					errorMessage = i18n.t("result.components.resultsPage.proctoringResultsViewTSX.fixErrorsToast", {
						questionNumbers: getQuestionNumbersWithErrors(latestValidatedDrafts, quizResponses).join(", "),
					}) as string;
				}
				coreToast.dismiss();
				coreToast.error(errorMessage);
				return false;
			} finally {
				setSavingGrade(undefined);
			}
		},
		[draftGrades, quizQuestions, quizResponses, session.sessionUuid],
	);

	const loading = useMemo(
		() => assignmentControl.loading || fetchingQuestions,
		[assignmentControl.loading, fetchingQuestions],
	);

	const totalPoints = quizQuestions.reduce((sum, q) => sum + q.correctionPoints, 0);
	const earnedPoints = quizResponses.reduce((sum, r) => sum + (r.score ?? 0), 0);
	const gradePercent = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;

	const hasUnsavedChanges = useMemo(() => {
		return quizResponses.some((original) => {
			const draft = draftGrades[original.id];

			if (!draft) return false;

			return draft.score !== String(original.score) || draft.notes !== original.notes;
		});
	}, [quizResponses, draftGrades]);

	const hasUnpublishedChanges = useMemo(() => {
		return quizResponses.some((r) => r.hasUnpublishedChanges);
	}, [quizResponses]);

	const bannerConfig = useMemo(() => {
		const shouldShowIsSyncedBanner = !hasUnpublishedChanges && !hasUnsavedChanges;
		const shouldShowUnsavedBanner = hasUnpublishedChanges;
		const syncLms = Boolean(verityAssessment.syncGradebook);

		let text: string | undefined = undefined;
		let bannerType: "success" | "secondary" | undefined = undefined;

		if (shouldShowIsSyncedBanner) {
			text = syncLms
				? (i18n.t("result.components.resultsPage.proctoringResultsViewTSX.gradeSyncLmsBanner") as string)
				: (i18n.t("result.components.resultsPage.proctoringResultsViewTSX.gradeSyncBanner") as string);
			bannerType = "success";
		} else if (shouldShowUnsavedBanner) {
			text = i18n.t("result.components.resultsPage.proctoringResultsViewTSX.unsavedChangesBanner") as string;
			bannerType = "secondary";
		}

		return { text, bannerType };
	}, [hasUnpublishedChanges, hasUnsavedChanges, verityAssessment.syncGradebook]);

	const closeUnsavedChangesModal = () => {
		setUnsavedModalShowing(false);
		setPendingAction(null);
	};

	/**Stores pending action. If there is unsaved changes will show a modal. If none, will do the passed in function. */
	const handleAction = useCallback(
		(action: () => void | Promise<void>) => {
			if (!hasUnsavedChanges) {
				void action();
				return;
			}

			setPendingAction(() => action);
			setUnsavedModalShowing(true);
		},
		[hasUnsavedChanges],
	);

	/**Handles save button in the unsaved changes modal. After saving will execute the pending action if any. */
	const handleSaveInModal = useCallback(
		async (publish: boolean) => {
			const success = await handleSaveGrade(publish);
			if (success && pendingAction) {
				await pendingAction();
				setPendingAction(null);
			}
			setUnsavedModalShowing(false);
		},
		[handleSaveGrade, pendingAction],
	);

	/**Handles discard changes button in the unsaved changes modal. After discarding aka all state will be lost, will execute the pending action if any. */
	const handleDiscardChangesModal = useCallback(async () => {
		if (pendingAction) {
			await pendingAction();
			setPendingAction(null);
		}
		setUnsavedModalShowing(false);
	}, [pendingAction]);

	const validatedDraftGrades = useMemo(() => {
		return validateDraftGrades(draftGrades, quizResponses, quizQuestions);
	}, [draftGrades, quizResponses, quizQuestions]);

	const formHasErrors = useMemo(() => {
		return hasErrors(validatedDraftGrades);
	}, [validatedDraftGrades]);

	const handleDraftGradeChange = useCallback((responseId: number, field: "score" | "notes", value: string) => {
		setDraftGrades((prev) => ({
			...prev,
			[responseId]: {
				...prev[responseId],
				[field]: value,
			},
		}));
	}, []);

	const saveDisabled = useMemo(() => savingGrade !== undefined || formHasErrors, [savingGrade, formHasErrors]);

	return {
		numAttemptsText,
		assignmentControl,
		loading,
		isLateSubmission,
		quizQuestions,
		quizResponses,
		savingGrade,
		totalPoints,
		earnedPoints,
		gradePercent,
		handleAction,
		handleSaveGrade,
		hasUnsavedChanges,
		saveDisabled,
		modal: {
			close: closeUnsavedChangesModal,
			open: unsavedModalShowing,
			save: handleSaveInModal,
			discard: handleDiscardChangesModal,
		},
		grade: {
			drafts: validatedDraftGrades,
			onChange: handleDraftGradeChange,
		},
		banner: bannerConfig,
	};
}

export type UseVerityAssessmentResultReturn = ReturnType<typeof useVerityAssessmentResult>;
